<template>
  <div role="list" class="mt-4 space-y-2">
    <PaginatedList :load="loadAppointments" :query-key="queryKey">
      <template #entry="{ entry: appointment }">
        <WorkshopAppointmentListItem :appointment="appointment" :clickable="clickable" />
      </template>
    </PaginatedList>
  </div>
  <div class="my-4 flex flex-col gap-4 md:grid md:grid-cols-3">
    <div class="col-start-3 flex flex-col gap-1">
      <h4 class="flex font-normal">
        <span>{{ t('totalCostCar') }}</span>
      </h4>
      <p class="font-semibold">{{ formatCurrency(totalCosts) }}</p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatCurrency } from '@/utils/format-numbers';
import PaginatedList from '@/components/PaginatedList.vue';
import { workshopAppointmentsService } from '@/api/workshop-appointments.service';
import WorkshopAppointmentListItem from './WorkshopAppointmentListItem.vue';
import { useWorkshopAppointmentTotalCosts } from '@/queries/use-workshop-appointments';
import { AllocationStatus } from '@carvia/ros-client-types';

const props = withDefaults(
  defineProps<{
    carId: string;
    showPastWorkshopAppointments: boolean;
    clickable?: boolean;
  }>(),
  {
    clickable: true,
  },
);

const { t } = useI18n();

const query = computed(() => ({
  carId: props.carId,
  type: undefined,
  allocationStatuses: props.showPastWorkshopAppointments
    ? undefined
    : [AllocationStatus.NOT_CONFIRMED, AllocationStatus.CONFIRMED, AllocationStatus.HANDED_OVER],
}));
const queryKey = computed(() => ['workshop-appointments-summary', query.value]);
const loadAppointments = (page: number) =>
  workshopAppointmentsService.getAllWithQuery({
    ...query.value,
    page,
  });

const { data: totalCostsData } = useWorkshopAppointmentTotalCosts(computed(() => props.carId));
const totalCosts = computed(() => totalCostsData.value ?? 0);
</script>

<i18n lang="json">
{
  "en": {
    "totalCostCar": "Total costs of previous appointments"
  },
  "de": {
    "totalCostCar": "Gesamtkosten vergangene Termine"
  }
}
</i18n>
